import { useState, useRef, useMemo, useEffect, useContext, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { isEmpty, sortBy, isArray, orderBy, isEqual } from 'lodash'
import moment from 'moment'

import { Scheduler } from '@aldabil/react-scheduler'
import {
	Box,
	Dialog,
	DialogContent,
	DialogContentText,
	DialogTitle,
	DialogActions,
	Button,
	IconButton,
	Typography,
	TextField,
	Tooltip,
	CircularProgress,
	Select,
	MenuItem,
	InputLabel,
	FormControl,
} from '@mui/material'
import { Shortcut as ShortcutIcon } from '@mui/icons-material'
import { makeStyles } from '@mui/styles'
import { LoadingButton } from '@mui/lab'
import { Close, Delete, FileCopy, History, Reply, Visibility, Edit } from '@mui/icons-material'

import TileWrapper from '../../components/TileWrapper'
import tileApi from 'apis/disApi/tileApi'

import { IconThemeProvider, IconThemeContext } from 'custom-components/context/IconThemesContext'
import {
	isJson,
	logErrorMessage,
	convertArrayToObject,
} from '../../../../../utils/functions/helpers'
import { tileKeys } from '../../hooks/useTileQuery'
import { ENV_ACTIONS } from 'reducers/environmentReducer'

import DispatchSchedulerSettingsDialog from './DispatchSchedulerSettingDialog'
import DoformsSendDispatch from 'components/data/dispatch/DoformsSendDispatch'
import LoadingSpinner from 'custom-components/LoadingSpinner'
import DoformsPortal from '../../../../../custom-components/DoformsPortal'
import ToastAlert from 'components/core/Layouts/ToastAlert'
import useDashboardQuery from '../../hooks/useDashboardQuery'
import useSchedulerQuery from '../../hooks/useSchedulerQuery'
import { useTileDashboard } from '../../dashboard/Dashboard'

import {
	getAllProjects,
	getAllTeams,
	getProjectForms,
	getViewRecords,
} from 'components/data/dataServices'
import {
	getDevices,
	getDispatchSettings,
	updateAppointments,
} from 'components/core/services/environmentService'
import { getAutoUpdateFilters } from '../../helpers'
import {
	viewRecord,
	getPrintPreview,
	getRecordHistory,
	deleteRecord,
	executePostDispatchLink,
} from 'components/data/datagrid/recordsService'
import { capitalizeFirstLetter } from '../../../../../components/data/dataHelpers'
import { performRecordAction } from 'components/data/datagrid/recordsHelper'
import DoformsRecordHistory from 'components/data/datagrid/DoFormsRecordHistory'
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import enUS from 'date-fns/locale/en-US'
import { toDayjs } from 'utils/functions/doformsDateUtil'
import { enqueueSnackbar } from 'notistack'
import useMobileUnitsQuery from '../../hooks/useMobileUnitsQuery'
import { CustomEditor } from './DispatchSchedulerCustomEditor'
import { ACTION_TYPE } from './helper/constants'
import useProject from '../../hooks/useProject'

const useStyles = makeStyles(() => ({
	dialog: {
		'& .MuiButton-root': {
			textTransform: 'none !important',
		},
	},
	dialogTitle: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		textAlign: 'center',
	},
	eventRenderer: {
		'& .MuiPaper-root': {
			marginTop: '-4px',
			minHeight: '25px',
			height: '96%',
		},
		'& .rs_event_item': {
			marginTop: '-4px',
		},
	},
	schedulerContainer: {
		'& .MuiPaper-root': {
			marginTop: '-3px',
			minHeight: '25px',
		},
		'& .rs__event__item': {
			marginTop: '2px',
			right: '3px',
		},
		'& .MuiPaper-root:has(> div:empty)': {
			display: 'none !important',
		},
	},
	buttonDialog: {
		'& .MuiButtonBase-root': {
			padding: '5px 1px !important',
		},
	},
	icon: (props) => ({
		color: props.color,
		'&:hover': {
			color: props.active.color,
			backgroundColor: 'transparent',
		},
	}),
	exitConfirmation: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		textAlign: 'center',
		'& .MuiButton-root': {
			textTransform: 'none !important',
		},
	},
	paper: {
		position: 'absolute',
		padding: '16px 32px 24px',
		borderRadius: '5px',
	},
	hidden: {
		display: 'none',
	},
}))

export const EVENT_STATUS = {
	PENDING: 'pending',
	SCHEDULED: 'scheduled',
	EMAILED: 'emailed',
	SENT: 'sent',
	RECEIVED: 'received',
	VIEWED: 'viewed',
	OPENED: 'opened',
	REJECTED: 'rejected',
	COMPLETED: 'completed',
	RECALLED: 'recalled',
	ACKNOWLEDGED: 'acknowledged',
}

function DispatchSchedulerTile(props) {
	const { tile } = props

	const [t] = useTranslation()

	const { environment } = useSelector((state) => state)
	const dispatch = useDispatch()
	const iconTheme = environment.theme.icons
	const classes = useStyles(iconTheme)
	const { id: dashboardKey } = useParams()

	const queryClient = useQueryClient()
	const updateTileMutation = useMutation(tileApi.update, {
		onSuccess: () => queryClient.invalidateQueries(tileKeys.allWithKey(dashboardKey)),
	})
	const tileRef = useRef(null)
	const { dashboardKeyList } = useDashboardQuery({
		dashboardKey,
	})

	const settings = useMemo(() => {
		if (tile?.settings && isJson(tile?.settings)) {
			return JSON.parse(tile?.settings ?? '{}')
		}

		return {}
	}, [tile?.settings])

	const {
		tileWidth,
		projectFormInfo,
		schedulerRangeType,
		schedulerStartTime,
		schedulerEndTime,
		schedulerColorStatusSettings,
		linkedFields = {},
		otherOptionsChecked,
	} = settings

	const { selectedFields } = useTileDashboard()
	const { filterConfigs } = useMemo(
		() => getAutoUpdateFilters(linkedFields, selectedFields, dashboardKeyList),
		[linkedFields, selectedFields]
	)
	const schedulerBoxRef = useRef(null)
	const schedulerRef = useRef(null)

	// Remove id because it is random property and make app re-render many times
	const filterWithoutId = useMemo(
		() =>
			filterConfigs.map((item) => {
				const { id, ...rest } = item
				return rest
			}),
		[filterConfigs]
	)

	useEffect(() => {
		const { Mobile_number: mobileNumber } = convertArrayToObject(filterWithoutId)

		const device = environment?.devices?.find((device) => device.number === mobileNumber)

		setSelectedDevice(device || null)
	}, [environment?.devices, filterWithoutId])

	const formSelected = useMemo(() => {
		const allForms = environment?.forms
		const defaultFormInfo = {
			...projectFormInfo,
			key: projectFormInfo?.formKey,
			name: projectFormInfo?.formName,
		}
		if (isEmpty(allForms)) {
			return defaultFormInfo
		}

		const form = allForms.find((item) => item.key === projectFormInfo?.formKey)
		if (!form) {
			return defaultFormInfo
		}

		return {
			...form,
			projectKey: projectFormInfo?.projectKey,
		}
	}, [projectFormInfo, environment?.forms])

	// data states
	const [view, setView] = useState(schedulerRangeType || 'day')
	const [isAgenda, setIsAgenda] = useState(false)
	const [isAllowDispatch, setIsAllowDispatch] = useState(false)
	const [schedulerColorStatus, setSchedulerColorStatus] = useState(schedulerColorStatusSettings)
	const [events, setEvents] = useState([])
	const [mobileUnits, setMobileUnits] = useState([])
	const [settingsOpen, setSettingsOpen] = useState(false)
	const [alertInfo, setAlertInfo] = useState({
		open: false,
		text: '',
		type: 'success',
	})
	const [schedulerKey, setSchedulerKey] = useState(0)
	const [loading, setLoading] = useState(false)
	const [selectedDevice, setSelectedDevice] = useState(null)
	const [selectedDate, setSelectedDate] = useState(null)
	const [selectedDateDisplay, setSelectedDateDisplay] = useState(moment())
	const [defaultSchedulerStatus, setDefaultSchedulerStatus] = useState([])
	const [slotTime, setSlotTime] = useState('')
	const [hourFormat, setHourFormat] = useState('12')
	const defaultDuration = '0.5'

	useEffect(() => {
		schedulerRef?.current?.scheduler?.handleState(isAgenda, 'agenda')
		// setTimeout(() => {
		// 	setSchedulerKey((prev) => prev + 1)
		// }, 300)
	}, [isAgenda])
	useEffect(() => {
		const updateTodayButtonColor = () => {
			const todayBtn = document.querySelector('button[aria-label="Today"]')
			if (todayBtn) {
				const currentDateMoment = moment()
				const selectedDateMoment = moment(selectedDateDisplay)

				if (
					view === 'day' &&
					selectedDateMoment.format('MM/DD/YYYY') === currentDateMoment.format('MM/DD/YYYY')
				) {
					todayBtn.setAttribute('style', 'color: #1976d2 !important')
				} else {
					todayBtn.setAttribute('style', 'color: black !important')
				}
			}
		}
		updateTodayButtonColor()

		const observer = new MutationObserver(() => {
			updateTodayButtonColor()
		})

		observer.observe(document.body, { childList: true, subtree: true })
		return () => observer.disconnect()
	}, [selectedDateDisplay, view])

	useEffect(() => {
		const showAgenda = otherOptionsChecked.some((item) => item === 'showAgenda')
		setIsAgenda(showAgenda)
		if (schedulerRef.current?.scheduler) {
			if (schedulerRef.current.scheduler.agenda !== showAgenda) {
				schedulerRef.current.scheduler.handleState(showAgenda, 'agenda')
			}
		}
	}, [otherOptionsChecked])
	useEffect(() => {
		const allowDispatch = otherOptionsChecked.some((item) => item === 'allowDispatch')
		setIsAllowDispatch(allowDispatch)
	}, [otherOptionsChecked])

	useEffect(() => {
		if (schedulerRef.current?.scheduler) {
			if (schedulerRef.current.scheduler.agenda !== isAgenda) {
				schedulerRef.current.scheduler.handleState(isAgenda, 'agenda')
			}
		}
	}, [schedulerKey])

	const { projectLoading } = useProject({ enabled: true })

	useEffect(() => {
		const timeFormat = environment?.userCurrent?.time?.timeFormat
		let format = '12'
		if (timeFormat && timeFormat.includes('HH')) {
			format = '24'
		}
		setHourFormat(format)
	}, [environment?.userCurrent?.time?.timeFormat])

	const isLoadedDevicesRef = useRef(false)
	useEffect(() => {
		const isLoadedDevices = isLoadedDevicesRef.current
		if (isLoadedDevices) return
		getDevices(environment.apiToken)
			.then((data) => {
				const devices = data.data
				dispatch({
					type: ENV_ACTIONS.GET_DEVICES,
					payload: devices,
				})
				initiateLoadAllTeams(devices)
				isLoadedDevicesRef.current = true
			})
			.catch((err) => {
				console.log(err)
			})
	}, [])

	const initiateLoadAllTeams = (devices) => {
		setLoading(true)
		loadAllTeams()
			.then((res) => {
				let teams = sortBy(res.data, (team) => team.name.toLowerCase())
				if (devices.length) {
					teams = teams.map((team) => {
						let teamDevices = devices.filter((device) => device.teamKey === team.key)
						teamDevices = sortBy(teamDevices, (team) => team.name.toLowerCase())
						return { ...team, devices: teamDevices }
					})
				}

				dispatch({
					type: ENV_ACTIONS.GET_TEAMS,
					payload: teams,
				})

				setLoading(false)
			})
			.catch((err) => {
				const errorMessage = 'Code ' + err.response?.data?.code + ': ' + err.response?.data?.message
				setLoading(false)
				setAlertInfo((prev) => ({
					...prev,
					open: true,
					type: 'error',
					text: errorMessage,
				}))
			})
	}

	const loadAllTeams = async () => {
		let promise = await getAllTeams(environment.apiToken)
		return promise
	}
	useEffect(() => {
		setView(schedulerRangeType)
		setSchedulerKey((prevKey) => prevKey + 1)
	}, [schedulerRangeType])

	useEffect(() => {
		setSchedulerColorStatus(schedulerColorStatusSettings)
		setSchedulerKey((prevKey) => prevKey + 1)
	}, [schedulerColorStatusSettings])

	const displayEvents = useMemo(() => {
		if (view === 'day' && !isAgenda) {
			const eventsMap = {}
			events.forEach((event) => {
				const hour = moment(event.start).format('HH')
				const currentValue = eventsMap[hour]
				if (!currentValue) {
					eventsMap[hour] = [event]
					return
				}
				eventsMap[hour] = [...currentValue, event]
			})
			if (isEmpty(eventsMap)) return []

			const eventWithMoreButton = Object.values(eventsMap)
				.map((eventsByHour) => {
					if (eventsByHour.length >= 4) {
						const total = eventsByHour.length - 3
						const lastEvent = eventsByHour[eventsByHour.length - 1]
						const eventButton = {
							title: `${total} More`,
							start: lastEvent.start,
							end: lastEvent.end,
							index: lastEvent.index,
							event_id: lastEvent.event_id + '_moreBtn',
							deletable: false,
							editable: false,
							draggable: false,
							status: '',
							onClick: () => {
								setIsAgenda(true)
								const hour = moment(eventsByHour[0].start).format('HH')
								setSlotTime(hour)
								schedulerRef?.current?.scheduler?.handleState(true, 'agenda')
							},
						}
						const result = eventsByHour.slice(0, 3)
						result.push(eventButton)
						return result
					}
					return eventsByHour
				})
				.flat()

			return eventWithMoreButton
		}

		if (view === 'day' && isAgenda && !isEmpty(slotTime)) {
			const filteredEvents = events.filter((event) => moment(event.start).format('HH') === slotTime)
			return filteredEvents
		}
		events.forEach((event) => {
			event.agendaView = isAgenda
		})
		return events
	}, [isAgenda, view, events])

	function getColorStatusDispatch(status) {
		if (!schedulerColorStatus) return ''
		const statusColor = schedulerColorStatus.find((item) => item.label === status)
		if (!statusColor) return ''
		return statusColor.color
	}

	function getActionsDispatch(status) {
		const actions = [ACTION_TYPE.COPY, ACTION_TYPE.VIEW, ACTION_TYPE.HISTORY]
		if (
			status === EVENT_STATUS.PENDING ||
			status === EVENT_STATUS.SCHEDULED ||
			status === EVENT_STATUS.REJECTED ||
			status === EVENT_STATUS.RECALLED ||
			status === EVENT_STATUS.ACKNOWLEDGED ||
			status === EVENT_STATUS.COMPLETED
		) {
			actions.push(ACTION_TYPE.EDIT)
			actions.push(ACTION_TYPE.DELETE)
		}
		if (
			status === EVENT_STATUS.SENT ||
			status === EVENT_STATUS.EMAILED ||
			status === EVENT_STATUS.RECEIVED ||
			status === EVENT_STATUS.VIEWED ||
			status === EVENT_STATUS.OPENED
		) {
			actions.push(ACTION_TYPE.RECALL)
		}
		if (status === EVENT_STATUS.PENDING || status === EVENT_STATUS.SCHEDULED) {
			actions.push(ACTION_TYPE.DRAGDROP)
		}
		if (
			status === EVENT_STATUS.SCHEDULED ||
			status === EVENT_STATUS.SENT ||
			status === EVENT_STATUS.EMAILED
		) {
			actions.push(ACTION_TYPE.SENDSMSMAIL)
		}

		return actions
	}

	const { isLoading: isLoadingMobileUnits, data: groupedData } = useMobileUnitsQuery({
		tile,
	})
	useEffect(() => {
		if (groupedData.length === 0) return

		let mobileUnitList = []
		for (let i = 0; i < groupedData.length; i++) {
			const currentGroup = groupedData[i]
			for (let j = 0; j < currentGroup.devices.length; j++) {
				const currentDevice = currentGroup.devices[j]
				mobileUnitList.push({
					number: currentDevice.number,
					name: currentDevice.name,
				})
			}
		}
		setMobileUnits(mobileUnitList)
	}, [groupedData])

	const { appoinments, isLoadingData, refetch } = useSchedulerQuery({
		tileKey: tile.key,
		rangeType: view,
		selectedDevice: selectedDevice,
		selectedDateDisplay: selectedDateDisplay,
	})

	useEffect(() => {
		if (!appoinments) return

		let eventList = []
		for (let i = 0; i < appoinments.data.length; i++) {
			const currentData = appoinments.data[i]
			const startMoment = moment(`${currentData.date} ${currentData.time}`, 'YYYY-MM-DD HH:mm')
			const endMoment = startMoment.clone().add(currentData.duration, 'minutes')
			const actions = getActionsDispatch(currentData?.dispatch?.systemStatus?.toLowerCase())
			eventList.push({
				index: i,
				event_id: currentData.key,
				agendaView: isAgenda,
				title: isEmpty(currentData?.dispatch?.name)
					? currentData?.dispatch?.formName
					: currentData?.dispatch?.name,
				start: startMoment.toDate(),
				end: endMoment.toDate(),
				dispatch: currentData.dispatch,
				deletable: false,
				editable: true,
				nickName: isEmpty(currentData?.dispatch?.ownerName)
					? currentData?.dispatch?.ownerId
					: currentData?.dispatch?.ownerName,
				color: getColorStatusDispatch(currentData?.dispatch?.systemStatus),
				draggable: actions.includes(ACTION_TYPE.DRAGDROP),
				availableActions: actions,
			})
		}
		setEvents(orderBy(eventList, (event) => event.start))
	}, [appoinments, schedulerColorStatus, isAgenda])

	const isScheduleLoading = useMemo(
		() => loading || isLoadingData || isLoadingMobileUnits || projectLoading,
		[loading, isLoadingData, isLoadingMobileUnits, projectLoading]
	)

	useEffect(() => {
		setSchedulerKey((prev) => prev + 1)
	}, [isScheduleLoading])

	useEffect(() => {
		getDispatchSettings(environment.apiToken)
			.then((setting) => {
				setDefaultSchedulerStatus(setting?.data?.status)
			})
			.catch((err) => {
				console.log(err)
			})
	}, [])

	const handleOpenDialog = () => {
		setSettingsOpen(true)
	}

	const handleCloseDialog = () => {
		setSettingsOpen(false)
	}

	const handleSubmitSetting = async (data) => {
		try {
			const {
				projectFormInfo,
				schedulerRangeType,
				schedulerStartTime,
				schedulerEndTime,
				schedulerColorStatusSettings,
				linkedFields,
				otherOptionsChecked,
			} = data
			await updateTileMutation.mutateAsync({
				dashboardKey,
				tileKey: tile.key,
				data: {
					settings: JSON.stringify({
						...settings,
						projectFormInfo,
						schedulerRangeType,
						schedulerStartTime,
						schedulerEndTime,
						schedulerColorStatusSettings,
						linkedFields,
						otherOptionsChecked,
					}),
				},
				token: environment.apiToken,
			})
		} catch (error) {
			logErrorMessage(error)
		} finally {
			handleCloseDialog()
		}
	}

	const handleResizeTileWidth = async (width) => {
		try {
			const editedSettings = JSON.stringify({
				...settings,
				tileWidth: width,
			})

			await updateTileMutation.mutateAsync({
				dashboardKey,
				tileKey: tile.key,
				data: { settings: editedSettings },
				token: environment.apiToken,
			})
		} catch (error) {
			logErrorMessage(error)
		}
	}

	const handleEventAdd = (newEvent) => {
		setEvents([...events, newEvent])
	}

	const handleEventUpdate = (updatedEvent) => {
		const updatedEvents = events.map((event) =>
			event.event_id === updatedEvent.event_id ? updatedEvent : event
		)
		setEvents(updatedEvents)
	}

	const handleEventDelete = (eventId) => {
		const updatedEvents = events.filter((event) => event.event_id !== eventId)
		setEvents(updatedEvents)
	}

	const handleViewChange = (view, agenda) => {
		setView(view)
		setIsAgenda(agenda)
		setSlotTime('')
	}

	const handleCellClick = (source) => {
		if (checkNotValidDateTime(source)) return
		if (view !== 'month') {
			setSelectedDate(moment(source).format('YYYY-MM-DDTHH:mm:ss'))
		} else {
			const sourceMoment = moment(source)
			const momentToday = moment().clone()
			if (momentToday.isBefore(sourceMoment, 'day')) {
				const selectMoment = sourceMoment
					.clone()
					.startOf('day')
					.add(8, 'hours')
					.format('YYYY-MM-DDTHH:mm:ss')
				setSelectedDate(selectMoment)
			} else {
				const currentMinute = momentToday.clone().minutes()
				let selectMoment
				if (currentMinute < 30) {
					selectMoment = momentToday.clone().startOf('hour').add(30, 'minutes')
				} else {
					selectMoment = momentToday
						.clone()
						.add(1, 'hour')
						.set({ minute: 0, second: 0, millisecond: 0 })
				}
				setSelectedDate(selectMoment)
			}
		}
		dispatch({
			type: ENV_ACTIONS.SHOW_SEND_DISPATCH,
			payload: 'dashboard',
		})
	}

	const checkNotValidDateTime = (date) => {
		const isNotValid = moment(date).isBefore(moment(), view !== 'month' ? null : 'day')
		if (isNotValid) {
			setAlertInfo((prev) => ({
				...prev,
				open: true,
				type: 'error',
				text: 'You can not schedule dispatch in the past',
			}))
		}
		return isNotValid
	}
	const handleEventDrop = async (event, droppedOn, updatedEvent, originalEvent) => {
		if (checkNotValidDateTime(droppedOn)) return

		const sourceMoment = moment(droppedOn)
		let selectMoment
		if (view !== 'month') {
			selectMoment = sourceMoment.clone()
		} else {
			const momentToday = moment().clone()
			if (momentToday.isBefore(sourceMoment, 'day')) {
				selectMoment = sourceMoment.clone().startOf('day').add(8, 'hours')
			} else {
				const currentMinute = momentToday.clone().minutes()
				if (currentMinute < 30) {
					selectMoment = momentToday.clone().startOf('hour').add(30, 'minutes')
				} else {
					selectMoment = momentToday
						.clone()
						.add(1, 'hour')
						.set({ minute: 0, second: 0, millisecond: 0 })
				}
			}
		}
		const updatedEvents = events.map((event) =>
			event.event_id === updatedEvent.event_id ? updatedEvent : event
		)

		try {
			setLoading(true)
			const res = await updateAppointments(environment.apiToken, updatedEvent.event_id, {
				date: selectMoment.format('YYYY-MM-DD'),
				time: selectMoment.format('HH:mm'),
			})
			if (res.status === 200) {
				setEvents(updatedEvents)
			}
			setLoading(false)
		} catch (err) {
			const errorMessage = 'Code ' + err.response?.data?.code + ': ' + err.response?.data?.message
			setAlertInfo((prev) => ({
				...prev,
				open: true,
				type: 'error',
				text: errorMessage,
			}))
			setLoading(false)
		}
	}

	const [selectedEvent, setSelectedEvent] = useState(null)
	const [openCustomEditor, setOpenCustomEditor] = useState(false)

	const [action, setAction] = useState('')

	// Memoized CustomViewer for Agenda view
	const memoizedCustomViewer = useCallback(
		() =>
			isAgenda &&
			selectedEvent && (
				<CustomViewer
					isFromScheduler={false}
					selectedEvent={selectedEvent}
					setSelectedEvent={setSelectedEvent}
					event={selectedEvent}
					close={() => setSelectedEvent(null)}
					open={!!selectedEvent}
					hourFormat={hourFormat}
					mobileUnits={mobileUnits}
					setAlertInfo={setAlertInfo}
					setAction={setAction}
					setOpenCustomEditor={setOpenCustomEditor}
					refetchAppointments={refetch}
				/>
			),
		[isAgenda, selectedEvent, hourFormat, mobileUnits]
	)

	const CustomLoader = () => {
		return (
			<div style={{ textAlign: 'center', padding: '20px' }}>
				<LoadingSpinner />
			</div>
		)
	}

	const titleSuffix = useMemo(() => {
		if (isEmpty(selectedDevice)) return ''

		return selectedDevice?.name || selectedDevice?.number
	}, [tile?.i, selectedDevice])

	return (
		<IconThemeProvider values={iconTheme}>
			<TileWrapper
				title={isEmpty(titleSuffix) ? tile?.i : `${tile?.i}  - ${titleSuffix}`}
				onSettingClick={handleOpenDialog}
				ref={tileRef}
				isExpandDialogBtn
			>
				<DispatchSchedulerSettingsDialog
					tileElementWidth={tileRef?.current?.clientWidth}
					defaultTileWidth={tileWidth}
					tile={tile}
					settings={settings}
					isSubmitting={updateTileMutation.isLoading}
					open={settingsOpen}
					onClose={handleCloseDialog}
					onSubmit={handleSubmitSetting}
					onResizeTileWidth={handleResizeTileWidth}
					dashboardKeyList={dashboardKeyList}
				/>

				<Box
					ref={schedulerBoxRef}
					sx={{
						position: 'absolute',
						left: 0,
						right: 0,
						top: 30,
						bottom: 0,
						overflowY: 'auto',
						height: '95%',
						background: '#fff',

						'& .MuiDataGrid-selectedRowCount': {
							opacity: '0 !important',
						},
						'*': {
							boxSizing: 'content-box !important',
						},
					}}
				>
					{environment?.isLoadingProjects ? (
						<LoadingSpinner />
					) : (
						<div className={classes.schedulerContainer}>
							<ToastAlert alertInfo={alertInfo} setAlertInfo={setAlertInfo} />
							{isAllowDispatch && (
								<div>
									<LoadingButton
										id={'send-dispatch'}
										className={classes.icon}
										loadingPosition="start"
										onClick={(e) => {
											dispatch({
												type: ENV_ACTIONS.SHOW_SEND_DISPATCH,
												payload: 'dashboard',
											})
										}}
										startIcon={<ShortcutIcon />}
									>
										{t('common:filters.dispatch')}
									</LoadingButton>
								</div>
							)}
							<Scheduler
								key={schedulerKey}
								ref={schedulerRef}
								view={view}
								alwaysShowAgendaDays={true}
								events={displayEvents}
								hourFormat={hourFormat}
								selectedDate={selectedDateDisplay}
								loading={isScheduleLoading}
								loadingComponent={<CustomLoader />}
								onViewChange={(view, agenda) => {
									handleViewChange(view, agenda)
								}}
								onEventAdd={handleEventAdd}
								onEventUpdate={handleEventUpdate}
								onEventDelete={handleEventDelete}
								onCellClick={handleCellClick}
								onEventDrop={(event, droppedOn, updatedEvent, originalEvent) => {
									handleEventDrop(event, droppedOn, updatedEvent, originalEvent)
								}}
								onSelectedDateChange={(date) => {
									setIsAgenda(false)
									setSlotTime('')
									setSelectedDateDisplay(date)
								}}
								day={{
									startHour: schedulerStartTime || 8,
									endHour: schedulerEndTime || 18,
								}}
								week={{
									startHour: schedulerStartTime || 8,
									endHour: schedulerEndTime || 18,
									weekStartOn: 0,
								}}
								month={{
									weekStartOn: 0,
								}}
								editable={false}
								draggable={true}
								customViewer={(event, close) => {
									return (
										<CustomViewer
											isFromScheduler={true}
											selectedEvent={selectedEvent}
											setSelectedEvent={setSelectedEvent}
											event={event}
											close={() => {
												close()
												setSelectedEvent(null)
											}}
											open={true}
											hourFormat={hourFormat}
											mobileUnits={mobileUnits}
											setAlertInfo={setAlertInfo}
											setAction={setAction}
											setOpenCustomEditor={setOpenCustomEditor}
											refetchAppointments={refetch}
										/>
									)
								}}
								eventRenderer={({ event, ...props }) => {
									if (event.onClick) {
										return (
											<div
												style={{
													display: 'flex',
													justifyContent: 'center',
													alignItems: 'center',
													height: '100%',
													width: '100%',
													background: '#f0f0f0',
													cursor: 'pointer',
												}}
												onClick={event.onClick}
											>
												<Typography variant="body2" color="primary">
													{event.title}
												</Typography>
											</div>
										)
									}
									return (
										<div
											style={{
												height: '-webkit-fill-available',
												width: '-webkit-fill-available',
												background: event.color,
											}}
											{...props}
											onClick={(e) => {
												props.onClick(e)
												if (!isEqual(event, selectedEvent)) {
													setSelectedEvent(event)
												}
											}}
										>
											<div
												style={{
													display: 'flex',
													flexDirection: 'row',
													justifyContent: 'space-between',
													height: '-webkit-fill-available',
													width: '-webkit-fill-available',
													background: event.color,
													whiteSpace: 'pre-line',
												}}
											>
												<Typography
													variant="h6"
													fontSize="12px"
													padding="6px 6px"
													color="white"
													whiteSpace="pre-line"
												>
													{event.title}
												</Typography>
												<div>
													<Typography
														variant="h6"
														fontSize="12px"
														padding="6px 6px"
														color="white"
														whiteSpace="pre-line"
													>
														{event.nickName}
													</Typography>
												</div>
											</div>
											{event.agendaView && (
												<div
													style={{
														display: 'flex',
														flexDirection: 'row',
														justifyContent: 'space-between',
														height: '-webkit-fill-available',
														width: '-webkit-fill-available',
														background: event.color,
														whiteSpace: 'pre-line',
														marginTop: '-10px',
													}}
												>
													<Typography
														variant="body"
														fontSize="12px"
														padding="6px 6px"
														color="white"
														whiteSpace="pre-line"
													>
														{moment(event.start).format(hourFormat === '12' ? 'hh:mm A' : 'HH:mm')}{' '}
														- {moment(event.end).format(hourFormat === '12' ? 'hh:mm A' : 'HH:mm')}
													</Typography>
												</div>
											)}
										</div>
									)
								}}
							/>
							{memoizedCustomViewer()}

							{action && selectedEvent && formSelected && (
								<CustomEditor
									formSelected={formSelected}
									openCustomEditor={openCustomEditor}
									action={action}
									event={selectedEvent}
									close={() => {
										setAction('')
										setOpenCustomEditor(false)
									}}
								/>
							)}

							<DoformsSendDispatch
								onDialogOpen={handleCellClick}
								formSelected={formSelected}
								action={'send'}
								title={t('common:tabs.dispatchScheduler')}
								recordsLoading={false}
								source={'dashboard'}
								tab={'forms'}
								environment={environment}
								deviceSelected={selectedDevice}
								selectedDate={selectedDate || null}
								duration={defaultDuration}
								refreshData={refetch}
							/>
						</div>
					)}
				</Box>
			</TileWrapper>
		</IconThemeProvider>
	)
}

const CustomViewer = (props) => {
	const {
		isFromScheduler = false,
		selectedEvent,
		setSelectedEvent,
		event,
		close,
		open,
		hourFormat,
		mobileUnits,
		setAlertInfo,
		setOpenCustomEditor,
		setAction,
		refetchAppointments,
	} = props
	const [t] = useTranslation()
	const { environment } = useSelector((state) => state)
	const { iconTheme } = useContext(IconThemeContext)
	const classes = useStyles(iconTheme)
	const [locale, setLocale] = useState(enUS)

	const [isSaving, setIsSaving] = useState(false)
	const [eventStatus, setEventStatus] = useState(event.dispatch?.status)
	const [eventUser, setEventUser] = useState(event.dispatch?.ownerId)
	const [eventTitle, setEventTitle] = useState(event.title)
	const [eventStartTime, setEventStartTime] = useState(toDayjs(event.start))
	const [eventEndTime, setEventEndTime] = useState(toDayjs(event.end))

	useEffect(() => {
		if (!isFromScheduler || isEqual(selectedEvent, event)) return

		setSelectedEvent(event)
	}, [isFromScheduler, selectedEvent])

	const eventEditable =
		event?.dispatch?.status?.toLowerCase() === EVENT_STATUS.PENDING ||
		event?.dispatch?.status?.toLowerCase() === EVENT_STATUS.SCHEDULED
	const maxLength = 56
	const isLongText = eventTitle.length > maxLength
	useEffect(() => {
		const importLocaleFile = async () => {
			const localeToSet = await import(
				`date-fns/locale/${t('common:languages.dateFnsLocale')}/index.js`
			)
			setLocale(localeToSet.default)
		}

		if (locale.code !== t('common:languages.dateFnsLocale')) {
			importLocaleFile()
		}
	}, [t('common:languages.dateFnsLocale')])

	const handleSaveEvent = async () => {
		if (!eventStartTime || !eventEndTime) {
			enqueueSnackbar('Invalid time values.', { variant: 'error' })
			return
		}

		const duration = eventEndTime.diff(eventStartTime, 'minutes')
		if (duration <= 0) {
			enqueueSnackbar('Start time must be before end time.', { variant: 'error' })
			return
		}

		setIsSaving(true)
		try {
			const dateString = moment(event.start).format('YYYY-MM-DD')
			const timeString = eventStartTime.format('HH:mm')
			const payload = {
				date: dateString,
				time: timeString,
				duration,
			}

			await updateAppointments(environment.apiToken, event.event_id, payload)

			enqueueSnackbar('Updated successfully.', { variant: 'success' })
			if (refetchAppointments) {
				refetchAppointments()
			}
			close()
		} catch (err) {
			const errorMessage = 'Code ' + err.response?.data?.code + ': ' + err.response?.data?.message

			setAlertInfo({
				open: true,
				type: 'error',
				text: errorMessage,
			})
		} finally {
			setIsSaving(false)
		}
	}

	return (
		<Dialog open={open} onClose={close}>
			{isSaving && (
				<div
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						height: '100%',
						backgroundColor: 'rgba(255,255,255,0.5)',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						zIndex: 10,
					}}
				>
					<CircularProgress size={50} />
				</div>
			)}
			<div style={{ backgroundColor: event.color, minHeight: '40px' }}></div>
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: '8px',
					paddingRight: '8px',
				}}
			>
				<IconButton size="small" onClick={close}>
					<Close />
				</IconButton>
				<div className={classes.buttonDialog} style={{ display: 'flex' }}>
					{event.availableActions?.includes(ACTION_TYPE.HISTORY) && (
						<Tooltip title="History">
							<IconButton
								size="small"
								onClick={() => {
									setAction(ACTION_TYPE.HISTORY)
									setOpenCustomEditor(true)
								}}
							>
								<History />
							</IconButton>
						</Tooltip>
					)}
					{event.availableActions?.includes(ACTION_TYPE.VIEW) && (
						<Tooltip title="View">
							<IconButton
								size="small"
								onClick={() => {
									setAction(ACTION_TYPE.VIEW)
									setOpenCustomEditor(true)
								}}
							>
								<Visibility />
							</IconButton>
						</Tooltip>
					)}
					{event.availableActions?.includes(ACTION_TYPE.RECALL) && (
						<Tooltip title="Recall">
							<IconButton
								size="small"
								onClick={() => {
									setAction(ACTION_TYPE.RECALL)
									setOpenCustomEditor(true)
								}}
							>
								<Reply />
							</IconButton>
						</Tooltip>
					)}
					{event.availableActions?.includes(ACTION_TYPE.COPY) && (
						<Tooltip title="Copy">
							<IconButton
								size="small"
								onClick={() => {
									setAction(ACTION_TYPE.COPY)
									setOpenCustomEditor(true)
								}}
							>
								<FileCopy />
							</IconButton>
						</Tooltip>
					)}
					{event.availableActions?.includes(ACTION_TYPE.DELETE) && (
						<Tooltip title="Delete">
							<IconButton
								size="small"
								onClick={() => {
									setAction(ACTION_TYPE.DELETE)
									setOpenCustomEditor(true)
								}}
							>
								<Delete />
							</IconButton>
						</Tooltip>
					)}
					{event.availableActions?.includes(ACTION_TYPE.EDIT) && (
						<Tooltip title="Edit">
							<IconButton
								size="small"
								onClick={() => {
									setAction(ACTION_TYPE.EDIT)
									setOpenCustomEditor(true)
								}}
							>
								<Edit />
							</IconButton>
						</Tooltip>
					)}
				</div>
			</div>
			<DialogContent>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: '5px' }}>
					<TextField
						id="eventStatus"
						value={eventStatus}
						onChange={(e) => setEventStatus(e.target.value)}
						label={t('common:misc.status')}
						size="small"
						variant="outlined"
						disabled={true}
					/>
					<FormControl size="small" variant="outlined" fullWidth>
						<InputLabel id="datagrid-select-label">{t('common:nav.user')}</InputLabel>
						<Select
							labelId="datagrid-select-label"
							id="datagrid-select-small"
							value={eventUser}
							onChange={(e) => {
								setEventUser(e.target.value)
							}}
							disabled={!eventEditable}
							label={t('common:nav.user')}
							MenuProps={{
								PaperProps: {
									style: {
										maxHeight: 300, // Giới hạn chiều cao dropdown
										width: 300, // Giới hạn chiều rộng dropdown
										overflow: 'auto',
									},
								},
							}}
						>
							{mobileUnits.map((mb) => (
								<MenuItem value={mb.number} key={mb.number}>
									{mb.number + ' - ' + mb.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<Tooltip
						title={
							isLongText ? (
								<Typography sx={{ fontSize: '16px', fontWeight: 'bold' }}>{eventTitle}</Typography>
							) : (
								''
							)
						}
						arrow
					>
						<TextField
							id="eventTitle"
							value={eventTitle}
							onChange={(e) => setEventTitle(e.target.value)}
							label={t('common:misc.recordName')}
							size="small"
							variant="outlined"
							disabled={true}
						/>
					</Tooltip>
					<div></div>
					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							gap: '8px',
						}}
					>
						<Typography variant="body1" color="textSecondary" paddingTop="17px">
							📅 {moment(event.start).format('MM/DD/YYYY')}
						</Typography>
						<LocalizationProvider dateAdapter={AdapterDayjs} locale={locale}>
							<TimePicker
								label={t('common:misc.startTimeScheduler')}
								value={toDayjs(eventStartTime)}
								onChange={(newValue) => setEventStartTime(newValue)}
								sx={{ width: '150px' }}
								disabled={!eventEditable}
								ampm={hourFormat === '12'}
								format={hourFormat === '12' ? 'hh:mm A' : 'HH:mm'}
							/>
						</LocalizationProvider>
						<LocalizationProvider dateAdapter={AdapterDayjs} locale={locale}>
							<TimePicker
								label={t('common:misc.endTimeScheduler')}
								value={toDayjs(eventEndTime)}
								onChange={(newValue) => setEventEndTime(newValue)}
								sx={{ width: '150px' }}
								disabled={!eventEditable}
								ampm={hourFormat === '12'}
								format={hourFormat === '12' ? 'hh:mm A' : 'HH:mm'}
							/>
						</LocalizationProvider>
					</div>
					{eventEditable && (
						<div
							style={{
								display: 'flex',
								justifyContent: 'end',
								gap: '8px',
							}}
						>
							<Button onClick={handleSaveEvent} disabled={isSaving} sx={{ fontSize: '18px' }}>
								{t('common:misc.saveAndClose')}
							</Button>
						</div>
					)}
				</Box>
			</DialogContent>
		</Dialog>
	)
}

export default DispatchSchedulerTile
